import { PaymentMethod } from '@kick/types';
import { formatCOP, formatDistance, formatHora } from './cn';
import type { PedidoComercio } from './queries';

/**
 * Imprime la comanda del pedido.
 *
 * Se abre una ventana aparte con solo el ticket en vez de imprimir la página:
 * la cocina necesita el papel de 80 mm con lo justo —qué preparar y para
 * quién—, no una captura del tablero.
 */
export function imprimirComanda(pedido: PedidoComercio, comercio: string): void {
  const ventana = window.open('', '_blank', 'width=380,height=640');
  if (!ventana) return; // El navegador bloqueó la ventana; no hay nada que hacer.

  ventana.document.write(html(pedido, comercio));
  ventana.document.close();
  ventana.focus();
  // Tras imprimir (o cancelar) la ventana se cierra sola.
  ventana.print();
  ventana.close();
}

function html(p: PedidoComercio, comercio: string): string {
  const direccion = [
    p.deliveryAddress.addressLine,
    p.deliveryAddress.details,
    p.deliveryAddress.neighborhood,
  ]
    .filter(Boolean)
    .join(' · ');

  const lineas = p.items
    .map(
      (item) => `
        <tr>
          <td class="cant">${item.quantity}×</td>
          <td>
            ${escapar(item.productName)}
            ${item.options.length ? `<div class="sub">${escapar(item.options.map((o) => o.optionName).join(' · '))}</div>` : ''}
            ${item.notes ? `<div class="nota">« ${escapar(item.notes)} »</div>` : ''}
          </td>
          <td class="precio">${formatCOP(item.lineTotal)}</td>
        </tr>`,
    )
    .join('');

  return `<!doctype html>
<html lang="es">
<head>
<meta charset="utf-8">
<title>Comanda ${p.code}</title>
<style>
  @page { size: 80mm auto; margin: 4mm; }
  * { box-sizing: border-box; }
  body { font: 12px/1.35 ui-monospace, "Courier New", monospace; color: #000; margin: 0; }
  h1 { font-size: 18px; margin: 0 0 2px; letter-spacing: 1px; }
  .centro { text-align: center; }
  .linea { border-top: 1px dashed #000; margin: 6px 0; }
  table { width: 100%; border-collapse: collapse; }
  td { vertical-align: top; padding: 2px 0; }
  .cant { width: 26px; font-weight: bold; }
  .precio { width: 66px; text-align: right; white-space: nowrap; }
  .sub { font-size: 11px; padding-left: 6px; }
  .nota { font-size: 11px; padding-left: 6px; font-style: italic; }
  .total { font-size: 15px; font-weight: bold; }
  .pie { font-size: 11px; margin-top: 8px; }
  dl { display: grid; grid-template-columns: auto 1fr; gap: 1px 8px; margin: 0; }
  dt { font-weight: bold; }
  dd { margin: 0; }
</style>
</head>
<body>
  <div class="centro">
    <h1>${escapar(p.code)}</h1>
    <div>${escapar(comercio)}</div>
    <div>${formatHora(p.placedAt ?? p.createdAt)}</div>
  </div>

  <div class="linea"></div>
  <table>${lineas}</table>
  <div class="linea"></div>

  <table>
    <tr><td>Subtotal</td><td class="precio">${formatCOP(p.subtotal)}</td></tr>
    <tr><td>Domicilio</td><td class="precio">${formatCOP(p.deliveryFee)}</td></tr>
    ${p.serviceFee > 0 ? `<tr><td>Tarifa lluvia</td><td class="precio">${formatCOP(p.serviceFee)}</td></tr>` : ''}
    ${p.tip > 0 ? `<tr><td>Propina</td><td class="precio">${formatCOP(p.tip)}</td></tr>` : ''}
    ${p.discountTotal > 0 ? `<tr><td>Descuento</td><td class="precio">-${formatCOP(p.discountTotal)}</td></tr>` : ''}
    <tr class="total"><td>TOTAL</td><td class="precio">${formatCOP(p.total)}</td></tr>
  </table>

  <div class="linea"></div>

  <dl>
    <dt>Pago</dt><dd>${escapar(estadoDelPago(p))}</dd>
    <dt>Entrega</dt><dd>${escapar(direccion)}</dd>
    ${p.deliveryAddress.contactPhone ? `<dt>Tel</dt><dd>${escapar(p.deliveryAddress.contactPhone)}</dd>` : ''}
    <dt>Distancia</dt><dd>${formatDistance(p.distanceMeters)}</dd>
  </dl>

  ${p.customerNotes ? `<div class="pie"><strong>Nota:</strong> ${escapar(p.customerNotes)}</div>` : ''}

  <div class="centro pie">Kick Domicilios</div>
</body>
</html>`;
}

/**
 * Cómo se cobra este pedido, en una línea.
 *
 * Es lo primero que mira quien empaca: una transferencia sin verificar no es
 * lo mismo que un pedido pagado, y confundirlos cuesta plata.
 */
function estadoDelPago(p: PedidoComercio): string {
  if (p.paymentStatus === 'PAID') return 'PAGADO';
  if (p.paymentMethod === PaymentMethod.TRANSFER) {
    return 'TRANSFERENCIA — sin verificar';
  }
  return 'EFECTIVO — COBRAR AL ENTREGAR';
}

/** El nombre de un producto o una nota pueden traer caracteres de HTML. */
function escapar(texto: string): string {
  return texto
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}
